import { useCallback, useEffect, useState } from 'react';
import api, { clinicalApi } from '../../services/api';
import type { PatientDiagnosis, PatientEncounter } from '../../services/api';

export type GranolaNote = {
  id: string;
  granola_id?: string;
  title?: string | null;
  web_url?: string | null;
  note_created_at?: string | null;
  note_updated_at?: string | null;
  summary_text?: string | null;
  summary_markdown?: string | null;
};

export interface PatientClinical {
  diagnoses: PatientDiagnosis[];
  encounters: PatientEncounter[];
  granolaNotes: GranolaNote[];
  loading: boolean;
  reload: () => void;
}

/**
 * Single source for a patient's read-only clinical journey data — diagnoses,
 * encounters (visit notes) and confirmed Granola notes — so the History
 * timeline and the clinical cards don't each refetch the same endpoints.
 * Every source fails open (an error just yields an empty list). Reloads when
 * the chart or diagnoses change so consumers stay live.
 */
export function usePatientClinical(spruceEntityId?: string): PatientClinical {
  const [diagnoses, setDiagnoses] = useState<PatientDiagnosis[]>([]);
  const [encounters, setEncounters] = useState<PatientEncounter[]>([]);
  const [granolaNotes, setGranolaNotes] = useState<GranolaNote[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!spruceEntityId) {
      setDiagnoses([]);
      setEncounters([]);
      setGranolaNotes([]);
      return;
    }
    setLoading(true);
    const [dx, enc, gran] = await Promise.allSettled([
      clinicalApi.listDiagnoses(spruceEntityId, 'all'),
      clinicalApi.listEncounters(spruceEntityId),
      api.get(`/granola/patient/${spruceEntityId}/notes`),
    ]);
    const rows = (r: PromiseSettledResult<any>) => {
      if (r.status !== 'fulfilled') return [];
      const data = r.value?.data;
      const arr = data?.success ? data.data : data;
      return Array.isArray(arr) ? arr : [];
    };
    setDiagnoses(rows(dx));
    setEncounters(rows(enc));
    setGranolaNotes(rows(gran));
    setLoading(false);
  }, [spruceEntityId]);

  useEffect(() => {
    load();
  }, [load]);

  // Stay in sync with edits made elsewhere on the same patient (the clinical
  // strip and the diagnosis manager both emit these after a successful save).
  useEffect(() => {
    if (!spruceEntityId) return;
    const reload = () => load();
    window.addEventListener('patientChartChange', reload);
    window.addEventListener('patientDiagnosesChange', reload);
    return () => {
      window.removeEventListener('patientChartChange', reload);
      window.removeEventListener('patientDiagnosesChange', reload);
    };
  }, [spruceEntityId, load]);

  return { diagnoses, encounters, granolaNotes, loading, reload: load };
}
